import { CommonModule } from "@angular/common";
import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { Subject, takeUntil } from "rxjs";
import { RestService } from "../../../core/services/rest.service";
import { ToasterService } from "../../../core/services/toaster-service";
import { MessageEnum } from "../../../core/utils/message.enum";
import {
  AccordionSection,
  AccordionTableViewComponent,
} from "../../common/accordion-table-view/accordion-table-view.component";
import { LoadingComponent } from "../../common/loading/loading.component";
import { ConnectionModalComponent } from "../connection-modal/connection-modal.component";

export interface GenerateTestDataModalResult {
  source: "manualFile" | "liveDb";
  fileType?: string;
  file?: any;
  dbConnection?: any;
  testSuite: string;
  testCases: any[];
  dataSource: "sourceFile" | "livedb";
}

@Component({
  selector: "app-generate-test-data-modal",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    AccordionTableViewComponent,
    LoadingComponent,
    ConnectionModalComponent,
  ],
  templateUrl: "./generate-test-data-modal.component.html",
  styleUrl: "./generate-test-data-modal.component.scss",
})
export class GenerateTestDataModalComponent implements OnInit, OnDestroy {
  @Input() projectId = "";
  @ViewChild(ConnectionModalComponent)
  connectionModal?: ConnectionModalComponent;

  readonly activeModal = inject(NgbActiveModal);
  private readonly restService = inject(RestService);
  private readonly toaster = inject(ToasterService);
  private readonly destroy$ = new Subject<void>();

  currentStep: 1 | 2 = 1;

  fileTypes = ["SQL", "CSV", "XLSX"];
  selectedFileType: string = this.fileTypes[0];
  selectedFile: any = null;
  selectedSource: "manualFile" | "liveDb" = "manualFile";
  liveDbEnabled = true;

  schemas: any[] = [];
  filteredSchemas: any[] = [];
  isLoading = false;
  loadError = false;

  showAddSchemaForm = false;
  newSchemaType: string = this.fileTypes[0];
  newSchemaName = "";
  newSchemaFile: File | null = null;
  isUploading = false;
  uploadError = "";

  dbConnections: any[] = [];
  selectedDbConnection: any = null;
  isLoadingDbConnections = false;
  dbConnectionsError = false;
  showAddDbForm = false;
  isSavingDbConnection = false;
  dbConnectionConfigs: Record<string, unknown> = {
    type: "DATABASE",
    title: "Add DB Connection",
    saveBtnText: "Add",
    isDynamic: true,
    isEdit: false,
    model: {},
  };

  accordionSections: AccordionSection[] = [];
  isLoadingSuites = false;
  suitesError = false;
  selectedRowsBySuite = new Map<string, any[]>();
  activeSuiteId: string | null = null;
  selectedTestCaseCount = 0;

  readonly testCaseColumns: any[] = [
    {
      display_name: "",
      key: "select",
      headerStyles: { width: "5%" },
      _config: { type: "checkbox", displayInColumn: true },
    },
    {
      display_name: "Test Case ID",
      key: "testCaseId",
      headerStyles: { width: "25%" },
    },
    {
      display_name: "Test Case",
      key: "testCase",
      headerStyles: { width: "70%" },
    },
  ];

  get stepLabel(): string {
    return `Step ${this.currentStep} of 2`;
  }

  get continueLabel(): string {
    return this.currentStep === 1 ? "Continue" : "Generate Data Table";
  }

  get canContinue(): boolean {
    if (this.currentStep === 1) {
      if (this.selectedSource === "manualFile") {
        return !!this.selectedFile && !this.showAddSchemaForm && !this.isUploading;
      }
      return !!this.selectedDbConnection && !this.showAddDbForm;
    }
    return this.selectedTestCaseCount > 0;
  }

  get canSaveNewDbConnection(): boolean {
    return !!this.connectionModal?.canSave && !this.isSavingDbConnection;
  }

  get selectedTestCases(): any[] {
    if (!this.activeSuiteId) {
      return [];
    }
    return this.selectedRowsBySuite.get(this.activeSuiteId) || [];
  }

  get canSaveNewSchema(): boolean {
    return !!this.newSchemaType && !!this.newSchemaFile && !this.isUploading;
  }

  get newSchemaAccept(): string {
    switch (this.newSchemaType) {
      case "SQL":
        return ".sql";
      case "CSV":
        return ".csv";
      case "XLSX":
        return ".xlsx";
      default:
        return ".sql,.csv,.xlsx";
    }
  }

  ngOnInit(): void {
    this.loadSchemas();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectSource(source: "manualFile" | "liveDb"): void {
    if (source === "liveDb" && !this.liveDbEnabled) {
      return;
    }
    this.selectedSource = source;
    if (source === "manualFile") {
      this.cancelAddDbForm();
    } else {
      this.cancelAddSchemaForm();
      this.loadDbConnections();
    }
  }

  openAddDbForm(): void {
    this.showAddDbForm = true;
    this.isSavingDbConnection = false;
    this.dbConnectionConfigs = {
      type: "DATABASE",
      title: "Add DB Connection",
      saveBtnText: "Add",
      isDynamic: true,
      isEdit: false,
      model: {},
    };
  }

  cancelAddDbForm(): void {
    this.showAddDbForm = false;
    this.isSavingDbConnection = false;
  }

  saveNewDbConnection(): void {
    if (!this.canSaveNewDbConnection) {
      return;
    }
    this.connectionModal?.submit();
  }

  onDbConnectionSaved(result: {
    value?: { connectionName?: string; meta?: Record<string, unknown> };
  }): void {
    if (this.isSavingDbConnection || !this.projectId || !result?.value) {
      return;
    }

    const connectionName = (result.value.connectionName || "").trim();
    const payload = {
      connectionName,
      connectionType: "DATABASE",
      meta: result.value.meta,
    };

    this.isSavingDbConnection = true;
    this.restService
      .createConnection(this.projectId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSavingDbConnection = false;
          this.showAddDbForm = false;
          this.selectedDbConnection = null;
          this.toaster.pop({
            type: "success",
            text: MessageEnum.CONNECTION_CREATE_SUCCESS,
          });
          this.loadDbConnections();
        },
        error: (error: { message?: string }) => {
          this.isSavingDbConnection = false;
          this.toaster.pop({
            type: "error",
            text: error?.message || MessageEnum.CONNECTION_CREATE_FAILURE,
          });
        },
      });
  }

  onFileTypeChange(): void {
    this.selectedFile = null;
    this.applyFileFilter();
  }

  openAddSchemaForm(): void {
    this.showAddSchemaForm = true;
    this.newSchemaType = this.selectedFileType || this.fileTypes[0];
    this.newSchemaName = "";
    this.newSchemaFile = null;
    this.uploadError = "";
  }

  cancelAddSchemaForm(): void {
    this.showAddSchemaForm = false;
    this.newSchemaName = "";
    this.newSchemaFile = null;
    this.uploadError = "";
    this.isUploading = false;
  }

  onNewSchemaFileSelected(event: Event): void {
    this.newSchemaFile = (event.target as HTMLInputElement).files?.[0] ?? null;
    this.uploadError = "";
    if (this.newSchemaFile) {
      this.newSchemaName = this.newSchemaFile.name;
      const ext = this.newSchemaFile.name.split(".").pop()?.toLowerCase();
      if (ext === "sql") {
        this.newSchemaType = "SQL";
      } else if (ext === "csv") {
        this.newSchemaType = "CSV";
      } else if (ext === "xlsx") {
        this.newSchemaType = "XLSX";
      }
    }
  }

  saveNewSchema(): void {
    if (!this.canSaveNewSchema || !this.projectId || !this.newSchemaFile) {
      return;
    }

    this.isUploading = true;
    this.uploadError = "";
    const uploadedName =
      this.newSchemaName.trim() || this.newSchemaFile.name;
    const uploadedType = this.newSchemaType;
    const localFile = this.newSchemaFile;

    const formData = new FormData();
    formData.append("type", "local");
    formData.append("name", uploadedName);
    formData.append("file", localFile, localFile.name);

    this.restService
      .createFileConnection(this.projectId, formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.isUploading = false;
          this.showAddSchemaForm = false;
          this.newSchemaName = "";
          this.newSchemaFile = null;
          this.selectedFileType = uploadedType;
          this.selectedFile =
            res?.data || {
              connectionName: uploadedName,
              meta: { fileName: uploadedName },
            };
          this.toaster.pop({
            type: "success",
            text: MessageEnum.FILE_UPLOAD_SUCCESS,
          });
          this.loadSchemas(uploadedName);
          this.goToStep2();
        },
        error: (error: { message?: string }) => {
          this.isUploading = false;
          this.uploadError =
            error?.message || MessageEnum.FILE_UPLOAD_FAILURE;
          this.toaster.pop({
            type: "error",
            text: error?.message || MessageEnum.FILE_UPLOAD_FAILURE,
          });
        },
      });
  }

  cancel(): void {
    this.activeModal.dismiss("cancel");
  }

  continue(): void {
    if (!this.canContinue) {
      return;
    }

    if (this.currentStep === 1) {
      this.goToStep2();
      return;
    }

    const selected = this.selectedTestCases;
    const suiteId = this.activeSuiteId || String(selected[0]?._suiteId ?? "");
    const suiteSection = this.accordionSections.find(
      (s) => String(s.id) === suiteId
    );
    const suiteName = suiteSection?.title || suiteSection?.testSuiteName || "";
    const suiteRows = this.selectedRowsBySuite.get(suiteId) || selected;

    const result: GenerateTestDataModalResult = {
      source: this.selectedSource,
      fileType:
        this.selectedSource === "manualFile" ? this.selectedFileType : undefined,
      file: this.selectedSource === "manualFile" ? this.selectedFile : undefined,
      dbConnection:
        this.selectedSource === "liveDb" ? this.selectedDbConnection : undefined,
      testSuite: suiteName,
      testCases: this.formatTestCases(suiteRows),
      dataSource:
        this.selectedSource === "liveDb" ? "livedb" : "sourceFile",
    };
    this.activeModal.close(result);
  }

  private goToStep2(): void {
    this.currentStep = 2;
    this.loadTestSuites();
  }

  goBack(): void {
    if (this.currentStep === 2) {
      this.currentStep = 1;
    }
  }

  onSectionExpand(event: { section: AccordionSection; typeId?: string }): void {
    const section = event?.section;
    if (!section || section.tableRows !== undefined) {
      return;
    }

    section.loading = true;
    section.tableColumns = this.testCaseColumns;
    this.accordionSections = [...this.accordionSections];

    this.restService
      .getTestCaseByVersionId(this.projectId, String(section.id))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const rows = (response?.data?.testCases || []).map((tc: any) => ({
            select: false,
            _id: tc._id,
            id: tc.testCaseId,
            testCaseId: tc.testCaseId,
            testCase: tc.testCase,
            name: tc.testCase,
            displayName: tc.testCaseId,
            testScenario: tc?.testScenario,
            expectedResult: tc?.expectedResult,
            prerequisites: tc?.prerequisites,
            key: tc?.key,
            storydesc: tc?.storydesc,
            storysummary: tc?.storysummary,
            _suiteId: section.id,
          }));
          section.tableRows = rows;
          section.testcaseCount = rows.length;
          section.itemCount = rows.length;
          section.loading = false;
          this.accordionSections = [...this.accordionSections];
        },
        error: () => {
          section.tableRows = [];
          section.loading = false;
          this.accordionSections = [...this.accordionSections];
        },
      });
  }

  onTableRowSelect(event: { section: AccordionSection; selectedRows: any }): void {
    const sectionId = String(event?.section?.id ?? "");
    if (!sectionId) {
      return;
    }

    // Another suite already has selections — ignore new picks until those are cleared.
    if (this.activeSuiteId && this.activeSuiteId !== sectionId) {
      this.restoreSuiteRowSelection(sectionId);
      return;
    }

    const raw = event?.selectedRows;
    const rows = (
      Array.isArray(raw)
        ? raw
        : raw?.sortedOutData || raw?.data || []
    ).filter((row: any) => row?.select !== false);

    const previousActive = this.activeSuiteId;

    if (rows.length) {
      this.activeSuiteId = sectionId;
      this.selectedRowsBySuite.set(
        sectionId,
        rows.map((row: any) => ({
          ...row,
          select: true,
          _suiteId: sectionId,
        }))
      );
    } else {
      this.selectedRowsBySuite.delete(sectionId);
      if (this.activeSuiteId === sectionId) {
        this.activeSuiteId = null;
      }
    }

    // Only update lock flags when the active suite changes (lock/unlock),
    // not on every checkbox toggle within the same suite.
    if (previousActive !== this.activeSuiteId) {
      this.syncSuiteSelectionLocks();
    }

    this.selectedTestCaseCount = this.selectedTestCases.length;
  }

  private restoreSuiteRowSelection(suiteId: string): void {
    const section = this.accordionSections.find((s) => String(s.id) === suiteId);
    if (!section?.tableRows?.length) {
      return;
    }
    // Mutate in place — do not replace section/table arrays (causes table flicker).
    section.tableRows.forEach((row: any) => {
      row.select = false;
    });
  }

  private syncSuiteSelectionLocks(): void {
    // Mutate existing section objects so accordion/table are not recreated.
    this.accordionSections.forEach((section) => {
      section.isSelected =
        !!this.activeSuiteId && String(section.id) === this.activeSuiteId;
    });
  }

  private clearSuiteSelections(suiteId: string): void {
    const section = this.accordionSections.find((s) => String(s.id) === suiteId);
    if (section?.tableRows?.length) {
      section.tableRows.forEach((row: any) => {
        row.select = false;
      });
    }
    this.selectedRowsBySuite.delete(suiteId);
    if (this.activeSuiteId === suiteId) {
      this.activeSuiteId = null;
    }
    this.syncSuiteSelectionLocks();
  }

  private loadTestSuites(): void {
    if (!this.projectId) {
      return;
    }
    this.isLoadingSuites = true;
    this.suitesError = false;
    this.accordionSections = [];
    this.selectedRowsBySuite.clear();
    this.activeSuiteId = null;
    this.selectedTestCaseCount = 0;

    this.restService
      .getTestCaseVersions(this.projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const suites = Array.isArray(response?.data) ? response.data : [];
          this.accordionSections = suites.map((suite: any) => {
            const suiteName = suite.versionName || suite.name || "Untitled Suite";
            return {
              id: suite._id,
              title: suiteName,
              name: suiteName,
              testSuiteName: suiteName,
              itemCount: suite.testcaseCount ?? 0,
              testcaseCount: suite.testcaseCount ?? 0,
              expanded: false,
              loading: false,
              isSelected: false,
            } as AccordionSection;
          });
          this.isLoadingSuites = false;
        },
        error: () => {
          this.isLoadingSuites = false;
          this.suitesError = true;
        },
      });
  }

  private formatTestCases(testCases: any[]): any[] {
    return (testCases || []).map((tc: any) => ({
      _id: tc?._id,
      testCaseId: tc?.testCaseId || tc?.id,
      testCase: tc?.testCase || tc?.name,
      testScenario: tc?.testScenario,
      expectedResult: tc?.expectedResult,
      prerequisites: tc?.prerequisites,
      key: tc?.key,
      storydesc: tc?.storydesc,
      storysummary: tc?.storysummary,
    }));
  }

  private loadSchemas(autoSelectName?: string): void {
    if (!this.projectId) {
      return;
    }
    this.isLoading = true;
    this.loadError = false;
    this.restService
      .getConnections(this.projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const allowedExt = [".sql", ".xlsx", ".csv"];
          const files = res?.data?.FILE || [];
          this.schemas = files.filter((c: any) => {
            const name = (c?.meta?.fileName || c?.connectionName || "")
              .toString()
              .toLowerCase();
            return allowedExt.some((ext) => name.endsWith(ext));
          });
          this.applyFileFilter();
          this.isLoading = false;

          if (autoSelectName) {
            const match =
              this.filteredSchemas.find((f: any) => {
                const name = (f?.meta?.fileName || f?.connectionName || "").toString();
                return name.toLowerCase() === autoSelectName.toLowerCase();
              }) ||
              this.schemas.find((f: any) => {
                const name = (f?.meta?.fileName || f?.connectionName || "").toString();
                return name.toLowerCase() === autoSelectName.toLowerCase();
              });
            if (match) {
              this.selectedFile = match;
            }
          }
        },
        error: () => {
          this.isLoading = false;
          this.loadError = true;
        },
      });
  }

  private loadDbConnections(autoSelectName?: string): void {
    if (!this.projectId) {
      return;
    }
    this.isLoadingDbConnections = true;
    this.dbConnectionsError = false;
    this.restService
      .getConnections(this.projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.dbConnections = Array.isArray(res?.data?.DATABASE)
            ? res.data.DATABASE
            : [];
          this.isLoadingDbConnections = false;

          if (autoSelectName) {
            const match = this.dbConnections.find(
              (c: any) =>
                (c?.connectionName || "").toString().toLowerCase() ===
                autoSelectName.toLowerCase()
            );
            if (match) {
              this.selectedDbConnection = match;
              return;
            }
          }

          if (
            this.selectedDbConnection &&
            !this.dbConnections.some(
              (c: any) =>
                (c?.id || c?._id) ===
                (this.selectedDbConnection?.id || this.selectedDbConnection?._id)
            )
          ) {
            this.selectedDbConnection = null;
          }
        },
        error: () => {
          this.isLoadingDbConnections = false;
          this.dbConnectionsError = true;
          this.dbConnections = [];
        },
      });
  }

  private applyFileFilter(): void {
    if (!this.selectedFileType) {
      this.filteredSchemas = this.schemas;
      return;
    }
    this.filteredSchemas = this.schemas.filter((f: any) =>
      this.isValidFile(f.meta?.fileName || f.connectionName, this.selectedFileType)
    );
  }

  private isValidFile(fileName: string, type: string): boolean {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    switch (type) {
      case "SQL":
        return ext === "sql";
      case "CSV":
        return ext === "csv";
      case "XLSX":
        return ext === "xlsx";
      default:
        return false;
    }
  }
}
